import { Button, Flex, FlexItem } from "mailup-web-components";
import React, { type FC } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilCallback, useRecoilValue } from "recoil";
import { WizardSteps } from "../model";
import {
  abortAssociationCallback,
  changeWizardStepCallback,
  goToCopyPasteStep2Callback,
  goToStep2Callback,
  saveImportConfigurationCallback,
} from "../recoil/callbacks";
import {
  importFileWizardStepState,
  step1CopyAndPasteEnable,
  step1EnabledState,
  step2EnabledState,
  step3EnabledState,
  step4EnabledState,
} from "../recoil/state/state";
import {
  copyPasteExtractedState,
  copyPasteProcessedState,
} from "../recoil/state/stateCopyPaste";
import { importTypeState } from "../recoil/state/stateStep1";
import { gainsightTrack } from "mailup-react-helpers";

const WizardStepButtons: FC = () => {
  const intl = useIntl();
  const { type } = useParams();

  const step = useRecoilValue(importFileWizardStepState);

  const changeWizardStep = useRecoilCallback(changeWizardStepCallback, []);
  const goToStep2 = useRecoilCallback(goToStep2Callback, []);
  const goToCopyPasteStep2 = useRecoilCallback(goToCopyPasteStep2Callback, []);
  const step1Enabled = useRecoilValue(step1EnabledState);
  const step2Enabled = useRecoilValue(step2EnabledState);
  const step3Enabled = useRecoilValue(step3EnabledState);
  const step4Enabled = useRecoilValue(step4EnabledState);
  const copyPasteStepEnabled = useRecoilValue(step1CopyAndPasteEnable);
  const navigate = useNavigate();
  const abortAssociation = useRecoilCallback(abortAssociationCallback, []);
  const saveImportConfiguration = useRecoilCallback(
    saveImportConfigurationCallback,
    [],
  );

  const importType = useRecoilValue(importTypeState);

  const save = () => {
    gainsightTrack("mup_importWizard_clickNextInFourthStep", {});

    saveImportConfiguration(type).then((importId) => {
      if (importId > 0) {
        navigate("/recipients/import/status");
      }
    });
  };

  switch (step) {
    case WizardSteps.CopyPaste:
      return (
        <Flex flexDirection="row" gap="16px" justifyContent="end">
          <Button
            onClick={() => {
              goToCopyPasteStep2();
            }}
            disabled={!copyPasteStepEnabled}
          >
            {intl.formatMessage({ id: "Load.Common.Forward" })}
          </Button>
        </Flex>
      );
    case WizardSteps.UploadFile:
      return (
        <Flex flexDirection="row" gap="16px" justifyContent="end">
          <Button
            onClick={() => {
              if (importType === "list") {
                gainsightTrack(
                  "mup_importWizard_clickNextInFirstStepChooseAFile",
                  {},
                );
              } else {
                gainsightTrack(
                  "mup_importWizard_clickNextInFirstStepFileUpload",
                  {},
                );
              }
              goToStep2();
            }}
            disabled={!step1Enabled}
          >
            {intl.formatMessage({ id: "Load.Common.Forward" })}
          </Button>
        </Flex>
      );
    case WizardSteps.Preview:
      return (
        <Flex flexDirection="row" gap="16px" justifyContent="space-between">
          <Button onClick={() => changeWizardStep(WizardSteps.UploadFile)}>
            {intl.formatMessage({ id: "Load.Common.Backwards" })}
          </Button>
          <Button
            onClick={() => {
              gainsightTrack("mup_importWizard_clickNextInSecondStep", {});

              changeWizardStep(WizardSteps.DynimicFields);
            }}
            disabled={!step2Enabled}
          >
            {intl.formatMessage({ id: "Load.Common.Forward" })}
          </Button>
        </Flex>
      );
    case WizardSteps.DynimicFields:
      return (
        <Flex flexDirection="row" gap="16px" justifyContent="space-between">
          <FlexItem>
            <Button onClick={() => changeWizardStep(WizardSteps.Preview)}>
              {intl.formatMessage({ id: "Load.Common.Backwards" })}
            </Button>
          </FlexItem>
          <FlexItem>
            <Flex gap="16px">
              <Button
                kind="danger"
                onClick={() => {
                  gainsightTrack(
                    "mup_importWizard_clickCancelAssociations",
                    {},
                  );

                  abortAssociation();
                }}
              >
                {intl.formatMessage({ id: "Load.FileImport.AbortAssociation" })}
              </Button>
              <Button
                onClick={() => {
                  gainsightTrack("mup_importWizard_clickNextInThirdStep", {});
                  changeWizardStep(WizardSteps.GroupsAndSettings);
                }}
                disabled={!step3Enabled}
              >
                {intl.formatMessage({ id: "Load.Common.Forward" })}
              </Button>
            </Flex>
          </FlexItem>
        </Flex>
      );
    case WizardSteps.GroupsAndSettings:
      return (
        <Flex flexDirection="row" gap="16px" justifyContent="space-between">
          <Button
            onClick={() =>
              changeWizardStep(
                type === "copyandpaste"
                  ? WizardSteps.CopyPaste
                  : WizardSteps.DynimicFields,
              )
            }
          >
            {intl.formatMessage({ id: "Load.Common.Backwards" })}
          </Button>
          <Button onClick={save} disabled={!step4Enabled}>
            {intl.formatMessage({ id: "Load.Common.Forward" })}
          </Button>
        </Flex>
      );
  }
};

export default WizardStepButtons;
